import type { TFunction } from 'i18next';
import type { OnboardingImportItem, OnboardingImportReport } from '../../api/account/accountApiTypes';
import type { AccountTenant, TenantServiceConfig } from '../../api/account/accountApiTypes';
import type { SetupProgress, ViewedSetupProgress } from '../../hooks/useSetupProgress';
import { resolveBookingPageTerms } from '../../pages/services/bookingPageTerms';
import { isSavedServiceEntry } from '../../pages/services/serviceConfigurationRules';
import { serviceTypeLabel } from '../../utils/useCaseLabels';
import { issueBody, itemLabel, missingFields, noticeText } from './importReportView';

/**
 * "Offene Punkte" of the onboarding import (PO, 2026-10-30): everything the
 * file did not set up — a skipped item, what the format cannot express
 * ("manuell anlegen"), and what the account still lacks to take bookings —
 * each with how critical it is and one sentence on what it means.
 *
 *   - **critical** (Kritisch) — kolula cannot work properly until it is done;
 *   - **important** (Wichtig) — it works, but customers or the tenant notice;
 *   - **optional** (Optional) — nice to have.
 *
 * ONE PLACE: the severity of every point is `OPEN_POINT_SEVERITY` below and
 * its sentence `onboarding_import.open_points.consequence.<code>` (de/en, parity
 * enforced by `keyParity.test.ts`). Each consequence was established from the
 * code that breaks without it; the evidence is listed per code in the PR that
 * introduced this file. Whether an account step is done is the dashboard's
 * own answer (`useSetupProgress`), passed in — never re-derived here.
 */

export type OpenPointSeverity = 'critical' | 'important' | 'optional';

export const SEVERITY_ORDER: readonly OpenPointSeverity[] = ['critical', 'important', 'optional'];

export type OpenPointCode =
    | 'legal_notice' | 'contact' | 'bank' | 'bank_optional' | 'booking_page' | 'no_room' | 'room_not_created' | 'prices_missing'
    | 'product_not_created' | 'terms'
    | 'terms_replace' | 'terms_additional' | 'terms_skipped'
    | 'services' | 'room_not_updated' | 'prices_not_updated' | 'product_not_updated' | 'class_skipped' | 'organization_skipped'
    | 'account_organization' | 'section_not_updated' | 'min_stay' | 'tax_category' | 'manual_entry' | 'unknown_section'
    | 'manual_change' | 'manual_skipped' | 'payment_default' | 'payment_missing'
    // The organisation import (docs/plans/organization-import.md §5.3); see ORGANIZATION_PART_POINTS.
    | 'organization_not_created' | 'unit_skipped' | 'contact_skipped' | 'contract_skipped' | 'owner_required'
    | 'contract_expired' | 'contract_price' | 'rooms_not_assigned' | 'room_twice' | 'owner_required_rooms'
    | 'contract_expired_rooms' | 'windows_not_created' | 'window_bookings' | 'window_ended' | 'window_list_all_ended'
    | 'column_not_imported' | 'type_value_unrecognised' | 'row_value_ignored' | 'section_elsewhere' | 'parts_elsewhere';

export const OPEN_POINT_SEVERITY: Readonly<Record<OpenPointCode, OpenPointSeverity>> = {
    // The account cannot take bookings properly yet.
    legal_notice: 'critical',
    contact: 'critical',
    bank: 'critical',
    booking_page: 'critical',
    no_room: 'critical',
    room_not_created: 'critical',
    prices_missing: 'critical',
    product_not_created: 'critical',
    terms: 'critical',
    payment_missing: 'critical',
    // Organisation import: the organisation, a room of a contract or the fixed times are not held for it.
    organization_not_created: 'critical',
    rooms_not_assigned: 'critical',
    windows_not_created: 'critical',
    // It works, but someone notices.
    services: 'important',
    room_not_updated: 'important',
    prices_not_updated: 'important',
    product_not_updated: 'important',
    class_skipped: 'important',
    organization_skipped: 'important',
    account_organization: 'important',
    section_not_updated: 'important',
    min_stay: 'important',
    tax_category: 'important',
    manual_entry: 'important',
    unknown_section: 'important',
    terms_replace: 'important',
    terms_additional: 'important',
    terms_skipped: 'important',
    payment_default: 'important',
    unit_skipped: 'important',
    contact_skipped: 'important',
    contract_skipped: 'important',
    owner_required: 'important',
    owner_required_rooms: 'important',
    contract_price: 'important',
    room_twice: 'important',
    window_bookings: 'important',
    row_value_ignored: 'important',
    section_elsewhere: 'important',
    parts_elsewhere: 'important',
    // Nice to have.
    bank_optional: 'optional',
    manual_change: 'optional',
    manual_skipped: 'optional',
    contract_expired: 'optional',
    contract_expired_rooms: 'optional',
    window_ended: 'optional',
    window_list_all_ended: 'optional',
    column_not_imported: 'optional',
    type_value_unrecognised: 'optional',
};

export interface OpenPoint {
    code: OpenPointCode;
    severity: OpenPointSeverity;
    /** What it is about: the item, the account section, the topic. */
    title: string;
    /** Why or what exactly, when there is more to say (the missing fields, the note). */
    detail?: string;
    /** The one sentence on what it means. */
    consequence: string;
}

/**
 * The dashboard's setup steps (`useSetupProgress`) for the tenant in view;
 * `null` while they are not loaded — then no account point is listed rather
 * than a guess.
 */
export interface SetupSnapshot {
    legalNotice: boolean;
    contactInfo: boolean;
    bankDetails: boolean;
    /** Some offer is paid by transfer or by an invoice kolula issues. */
    bankDetailsRequired: boolean;
    servicesConfigured: boolean;
    bookingPage: boolean;
    createVenue: boolean;
    /** The tenant already has an own terms document (`resolveBookingPageTerms` answers `own`). */
    ownTermsDocument: boolean;
    /** Each offered use case's payment arrangement as stored (`paymentArrangementsOf`). */
    paymentArrangements: UseCasePaymentArrangement[];
}

/**
 * One offered use case's payment arrangement, read the way Services › Use
 * Cases reads it: the stored entry of `payments.useCases`, and whether the
 * tenant saved it (`isSavedServiceEntry`) or kolula's template stored it on
 * switch-on (its template marker, ADR-0016 Amendment 8).
 *
 *   - `saved`   — the tenant decided it: no open point;
 *   - `seeded`  — kolula's default, never confirmed: Wichtig, shown with what it says;
 *   - `missing` — nothing stored: Kritisch while the use case would be charged,
 *                 because a customer mail of a paid booking then has no payment
 *                 route (the mail guard's `payment_settings_missing`).
 */
export interface UseCasePaymentArrangement {
    useCase: string;
    state: 'saved' | 'seeded' | 'missing';
    /** The stored entry; `null` exactly when `missing`. */
    entry: TenantServiceConfig | null;
}

/** The offered use cases' stored arrangements, in the order the tenant offers them. */
export const paymentArrangementsOf = (tenant: Pick<AccountTenant, 'services' | 'payments'>): UseCasePaymentArrangement[] =>
    (tenant.services ?? []).map((useCase) => {
        const entry = tenant.payments?.useCases?.[useCase.toLowerCase()] ?? null;
        if (!entry) return { useCase, state: 'missing', entry: null };
        return { useCase, state: isSavedServiceEntry(entry) ? 'saved' : 'seeded', entry };
    });

/**
 * The snapshot of the tenant in view: its checklist (`useViewedSetupProgress`)
 * and whether it already has an own terms document. `null` while the checklist
 * is not known yet — then no account point is guessed, and the Nutzungsordnung
 * is not called Kritisch for want of an answer.
 */
export const setupSnapshotOf = (
    progress: SetupProgress | null,
    tenant: AccountTenant | undefined,
): SetupSnapshot | null => {
    if (!progress || !tenant) return null;
    const ownTermsDocument = resolveBookingPageTerms({ ownTerms: tenant.ownTerms?.documents }).document === 'own';
    return {
        ...progress.stepsComplete,
        bankDetailsRequired: progress.bankDetailsRequired,
        ownTermsDocument,
        paymentArrangements: paymentArrangementsOf(tenant),
    };
};

export const point = (code: OpenPointCode, title: string, t: TFunction, detail?: string): OpenPoint => ({
    code,
    severity: OPEN_POINT_SEVERITY[code],
    title,
    ...(detail ? { detail } : {}),
    consequence: t(`onboarding_import.open_points.consequence.${code}`),
});

const errors = (item: OnboardingImportItem) => item.issues.filter((i) => i.level === 'error');

/** "fehlt: Straße, PLZ", or the first reason in words. */
const skipReason = (item: OnboardingImportItem, t: TFunction, language: string): string | undefined => {
    const missing = missingFields(item, t);
    if (missing.length > 0) return t('onboarding_import.open_points.missing', { fields: missing.join(', ') });
    const [first] = errors(item);
    return first ? issueBody(first, t, language) : undefined;
};

/** The file sets this account section in this run (not skipped). */
const fileSets = (items: OnboardingImportItem[], key: string): boolean =>
    items.some((i) => i.section === 'account' && i.key === key && !i.skipped && i.action !== 'unchanged');

const ACCOUNT_STEPS: ReadonlyArray<{ key: string, step: keyof SetupSnapshot, code: OpenPointCode }> = [
    { key: 'legalNotice', step: 'legalNotice', code: 'legal_notice' },
    { key: 'contact', step: 'contactInfo', code: 'contact' },
    { key: 'bankDetails', step: 'bankDetails', code: 'bank' },
];

/** What the account still lacks after this file (the dashboard's steps, plus what the file sets). */
const accountPoints = (report: Pick<OnboardingImportReport, 'items'>, setup: SetupSnapshot | null, t: TFunction, language: string): OpenPoint[] => {
    if (!setup) return [];
    const points: OpenPoint[] = [];
    for (const { key, step, code } of ACCOUNT_STEPS) {
        if (setup[step] || fileSets(report.items, key)) continue;
        // Bank details are Kritisch exactly when the dashboard requires them
        // (`bankDetailsRequiredForPayments`, via `useSetupProgress`); otherwise its step is optional, and so is this point.
        const level = key === 'bankDetails' && !setup.bankDetailsRequired ? 'bank_optional' : code;
        const skipped = report.items.find((i) => i.section === 'account' && i.key === key && i.skipped);
        points.push(point(level, t(`onboarding_import.account.${key}`), t, skipped ? skipReason(skipped, t, language) : undefined));
    }
    const roomCreated = report.items.some((i) => i.section === 'room' && i.action === 'create' && !i.skipped);
    if (!setup.createVenue && !roomCreated) points.push(point('no_room', t('onboarding_import.open_points.topic.no_room'), t));
    // With offered use cases, each says its own arrangement (`paymentPoints`), which is the same point made precisely.
    if (!setup.servicesConfigured && setup.paymentArrangements.length === 0) {
        points.push(point('services', t('onboarding_import.open_points.topic.services'), t));
    }
    if (!setup.bookingPage) points.push(point('booking_page', t('onboarding_import.open_points.topic.booking_page'), t));
    return points;
};

/** Built at runtime, so the key scan (`onboardingImportKeys.test.ts`) leaves it to the view tests. */
const PAYMENT_KEY = 'onboarding_import.open_points.payment.';

/** "kostenpflichtig, Vorauszahlung (Pre-Pay) per Überweisung" — the entry's three answers in plain words. */
export const paymentArrangementText = (entry: Pick<TenantServiceConfig, 'charging' | 'payment'>, t: TFunction): string => {
    const charging = t(`${PAYMENT_KEY}charging.${entry.charging}`);
    if (entry.charging === 'free' || !entry.payment) return charging;
    const separator = String(t('onboarding_import.open_points.payment.channel_separator'));
    const channels = entry.payment.channels.map((channel) => t(`${PAYMENT_KEY}channel.${channel}`)).join(separator);
    return t('onboarding_import.open_points.payment.summary', { charging, flow: t(`${PAYMENT_KEY}flow.${entry.payment.flow}`), channels });
};

/**
 * What is set now for each offered use case, for the tenant to confirm (PO,
 * 2026-10-30): Wichtig while it is kolula's seeded default, Kritisch while
 * nothing is stored, and nothing once the tenant saved it.
 *
 * "Nothing stored" is Kritisch without asking whether anything is charged,
 * because nothing stored is not an answer of "free": kolula's proposal for an
 * unsaved use case is its template (`PAYMENT_TEMPLATE`, kostenpflichtig), the
 * booking backends resolve such a use case to no arrangement at all
 * (`resolvePaymentArrangement` → `null`), and a priced booking's customer mail
 * then has no payment route to name. A use case the tenant decided to be free
 * is a saved entry and lists nothing.
 */
const paymentPoints = (setup: SetupSnapshot | null, t: TFunction): OpenPoint[] => {
    if (!setup) return [];
    return setup.paymentArrangements.flatMap(({ useCase, state, entry }): OpenPoint[] => {
        const title = serviceTypeLabel(t, useCase);
        if (state === 'seeded' && entry) {
            return [point('payment_default', title, t, t('onboarding_import.open_points.payment.seeded', { arrangement: paymentArrangementText(entry, t) }))];
        }
        if (state === 'missing') {
            return [point('payment_missing', title, t, t('onboarding_import.open_points.payment.missing'))];
        }
        return [];
    });
};

const PART_SECTIONS = new Set<OnboardingImportItem['section']>(['unit', 'contact', 'contract', 'access', 'window']);

const CREATE_OR_UPDATE: Partial<Record<OnboardingImportItem['section'], [OpenPointCode, OpenPointCode]>> = {
    room: ['room_not_created', 'room_not_updated'],
    price: ['prices_missing', 'prices_not_updated'],
    product: ['product_not_created', 'product_not_updated'],
    customerClass: ['class_skipped', 'class_skipped'],
    organization: ['organization_skipped', 'organization_skipped'],
    setting: ['min_stay', 'min_stay'],
    manual: ['manual_skipped', 'manual_skipped'],
    file: ['unknown_section', 'unknown_section'],
};

/**
 * The Nutzungsordnung the file brings is not set up: Kritisch only while the
 * tenant has no own terms document — with one, the booking page has its terms
 * (`bookingPageTermsVerdict`) and the file's document waits for its upload.
 * Which sentence fits follows the server's plan (`planTerms` matches by title):
 * `update` replaces the document of that title, `create` adds one beside the
 * existing — whoever imports, owner or master, uploads it (PO, 2026-10-30). An item
 * SKIPPED for an error (a title, no booking link, the file's shape) is not set
 * up however much is uploaded, so it never says "once uploaded": without own
 * terms it is the plain Kritisch point, with them the existing ones still
 * apply (`terms_skipped`). `null` while the account is loading: whether it is
 * Kritisch is not known yet.
 */
const termsCode = (item: OnboardingImportItem, setup: SetupSnapshot | null): OpenPointCode | null => {
    if (!setup) return null;
    if (!setup.ownTermsDocument) return 'terms';
    if (item.skipped) return 'terms_skipped';
    return item.action === 'update' ? 'terms_replace' : 'terms_additional';
};

const termsPoint = (item: OnboardingImportItem, setup: SetupSnapshot | null, t: TFunction, detail?: string): OpenPoint | null => {
    const code = termsCode(item, setup);
    return code ? point(code, itemLabel(item, t), t, detail) : null;
};

/**
 * Which import the report comes from. Both share this table; they differ in
 * one line, the organisation that is not created: on "Einrichtung per Datei"
 * organisations are one section of an account (Wichtig, decided 2026-10-30);
 * on "Organisationen importieren" the organisation is what the upload is for,
 * and its units, contacts, contracts, rooms and times go with it (Kritisch,
 * plan §5.3).
 */
export type OpenPointContext = 'account' | 'organizations';

/** The cause a part was skipped for: its own first error, or — skipped with what it depends on — that item's (`params.cause`). */
const causeOf = (item: OnboardingImportItem): { code: string, on: string | null } => {
    const [first] = errors(item);
    if (!first) return { code: '', on: null };
    if (first.code !== 'depends_on') return { code: first.code, on: null };
    return { code: String(first.params?.cause ?? ''), on: String(first.params?.section ?? '') };
};

/**
 * A skipped part of an organisation (organisation import), weighed by what
 * breaks (each line's evidence is in organizationParts.ts, plan §5.3):
 *
 *   - skipped with its organisation: nothing — the organisation's point says it;
 *   - a unit, a contact: Wichtig — the organisation works without it;
 *   - a contract: by why — `owner_required` Wichtig (the owner imports it),
 *     expired Optional (§8 Q9: nothing from before kolula), else Wichtig;
 *   - a room: Kritisch while it is not held for the organisation
 *     (`no_running_contract`, unknown, ambiguous, dedicated elsewhere, the
 *     contract not created) — except a contract the owner must import
 *     (Wichtig) or an expired one (Optional), via `params.cause`; named twice
 *     (it is assigned on the first contract): Wichtig;
 *   - fixed times: Kritisch — they are not reserved for the organisation; skipped
 *     with their room: nothing, the room's point says it.
 */
const partPoint = (
    item: OnboardingImportItem,
    title: string,
    reason: string | undefined,
    t: TFunction,
    skippedOrganizations: ReadonlySet<string>,
): OpenPoint | null => {
    const cause = causeOf(item);
    if (cause.on === 'organization' || (item.parent && skippedOrganizations.has(item.parent.organization))) return null;
    if (item.section === 'unit') return point('unit_skipped', title, t, reason);
    if (item.section === 'contact') return point('contact_skipped', title, t, reason);
    if (item.section === 'contract') return contractPoint(cause.code, title, reason, t);
    if (item.section === 'access') return roomPoint(cause, title, reason, t);
    // Times skipped with their room: the room's point says it.
    return cause.on === 'access' ? null : point('windows_not_created', title, t, reason);
};

/** A contract not taken over, by why: the owner imports it, it has ended, or anything else. */
const contractPoint = (cause: string, title: string, reason: string | undefined, t: TFunction): OpenPoint => {
    if (cause === 'owner_required') return point('owner_required', title, t);
    if (cause === 'contract_expired') return point('contract_expired', title, t, reason);
    return point('contract_skipped', title, t, reason);
};

/** A room of a contract not assigned: with a contract the owner imports or one that has ended, else not held for the organisation. */
const roomPoint = (cause: { code: string, on: string | null }, title: string, reason: string | undefined, t: TFunction): OpenPoint => {
    if (cause.on === 'contract' && cause.code === 'owner_required') return point('owner_required_rooms', title, t);
    if (cause.on === 'contract' && cause.code === 'contract_expired') return point('contract_expired_rooms', title, t);
    if (cause.code === 'room_twice') return point('room_twice', title, t, reason);
    return point('rooms_not_assigned', title, t, reason);
};

/** A skipped account section: the account's organisation, or a section the dashboard's checklist does not already list. */
const accountSectionPoint = (
    item: OnboardingImportItem,
    setup: SetupSnapshot | null,
    title: string,
    reason: string | undefined,
    t: TFunction,
): OpenPoint | null => {
    if (item.key === 'organization') return point('account_organization', title, t, reason);
    const step = ACCOUNT_STEPS.find((s) => s.key === item.key);
    // Not stored yet: the account point carries it, with this reason.
    if (step && setup && !setup[step.step]) return null;
    return point('section_not_updated', title, t, reason);
};

/** What belongs to the other import: organisation parts on "Einrichtung per Datei", account sections here. */
const elsewherePoint = (item: OnboardingImportItem, title: string, t: TFunction): OpenPoint | null => {
    if (errors(item).some((i) => i.code === 'organization_import_only')) return point('parts_elsewhere', title, t);
    if (item.section === 'file' && errors(item).some((i) => i.code === 'not_organization_section')) return point('section_elsewhere', title, t);
    return null;
};

/** A skipped item as an open point. An account section the dashboard already lists is left to `accountPoints`. */
const skippedPoint = (
    item: OnboardingImportItem,
    setup: SetupSnapshot | null,
    t: TFunction,
    language: string,
    context: OpenPointContext,
    skippedOrganizations: ReadonlySet<string>,
): OpenPoint | null => {
    // A price list skipped only because its room or product is: that point says it already.
    if (item.section === 'price' && errors(item).every((i) => i.code === 'depends_on')) return null;
    // Photos attached to a name the file does not have: nothing of the file is lost, the page says it on the row.
    if (errors(item).every((i) => i.code === 'photos_unknown_room')) return null;
    const reason = skipReason(item, t, language);
    const title = itemLabel(item, t);
    if (item.section === 'account') return accountSectionPoint(item, setup, title, reason, t);
    if (item.section === 'document') return termsPoint(item, setup, t, reason);
    const elsewhere = elsewherePoint(item, title, t);
    if (elsewhere) return elsewhere;
    if (PART_SECTIONS.has(item.section)) return partPoint(item, title, reason, t, skippedOrganizations);
    if (item.section === 'organization' && item.action === 'create' && context === 'organizations') {
        return point('organization_not_created', title, t, reason);
    }
    const codes = CREATE_OR_UPDATE[item.section];
    if (!codes) return null;
    return point(item.action === 'create' ? codes[0] : codes[1], title, t, reason);
};

/** Warnings that leave something to do by hand: a new room without prices, the Nutzungsordnung, "manuell ändern". */
const warningPoints = (item: OnboardingImportItem, setup: SetupSnapshot | null, t: TFunction, language: string): OpenPoint[] => {
    if (item.skipped) return [];
    const title = itemLabel(item, t);
    return item.issues.flatMap((issue): OpenPoint[] => {
        if (issue.level !== 'warning') return [];
        if (issue.code === 'no_prices' && item.action === 'create') return [point('prices_missing', title, t, issueBody(issue, t, language))];
        if (issue.code === 'terms_pdf_missing') {
            const terms = termsPoint(item, setup, t, issueBody(issue, t, language));
            return terms ? [terms] : [];
        }
        if (issue.code === 'address_kept' || issue.code === 'product_kept') return [point('manual_change', title, t, issueBody(issue, t, language))];
        const organizationWarning = ORGANIZATION_WARNINGS[issue.code];
        if (organizationWarning) return [point(organizationWarning, title, t, issueBody(issue, t, language))];
        return [];
    });
};

/**
 * The organisation import's warnings that leave something to do: a price only
 * in the contract's note (Wichtig, §8 Q8 "manuell anlegen"); others' bookings
 * inside new times (Wichtig, D2: listed and kept, the tenant decides); a time
 * that has ended, or a room whose times have all ended (Optional, §8 Q9 and
 * Q5: nothing from before kolula, the room keeps its current times).
 */
const ORGANIZATION_WARNINGS: Record<string, OpenPointCode> = {
    contract_price_in_note: 'contract_price',
    window_bookings: 'window_bookings',
    window_ended: 'window_ended',
    window_list_all_ended: 'window_list_all_ended',
};

const NOTICE_CODES: Record<string, OpenPointCode> = {
    tax_category_unset: 'tax_category',
    extras_tax_category_unset: 'tax_category',
    min_stay_manual: 'min_stay',
    min_stay_category_manual: 'min_stay',
};

/**
 * Every open point of a preview or an applied result, Kritisch first, in the
 * order found within a level. `setup` is the dashboard's checklist for the
 * tenant (null while loading).
 */
export const openPointsOf = (
    report: Pick<OnboardingImportReport, 'items' | 'manual' | 'notices'>,
    setup: SetupSnapshot | null,
    t: TFunction,
    language = 'de',
    context: OpenPointContext = 'account',
    /** Points the page adds from what only it knows (a sheet's columns and notes). */
    extra: OpenPoint[] = [],
): OpenPoint[] => {
    // A skipped organisation takes its parts with it; its own point says so, theirs would repeat it.
    const skippedOrganizations = new Set(report.items.filter((i) => i.section === 'organization' && i.skipped).map((i) => i.key));
    const points: OpenPoint[] = [
        ...accountPoints(report, setup, t, language),
        ...paymentPoints(setup, t),
        ...report.items.flatMap((item) => {
            const skipped = item.skipped ? skippedPoint(item, setup, t, language, context, skippedOrganizations) : null;
            return [...(skipped ? [skipped] : []), ...warningPoints(item, setup, t, language)];
        }),
        ...(report.notices ?? []).map((notice) => point(
            NOTICE_CODES[notice.code] ?? 'manual_entry',
            t(`onboarding_import.open_points.topic.${NOTICE_CODES[notice.code] ?? 'manual_entry'}`),
            t,
            noticeText(notice, t),
        )),
        ...report.manual.map((m) => point('manual_entry', m.topic, t, m.note)),
        ...extra,
    ];
    return SEVERITY_ORDER.flatMap((severity) => points.filter((p) => p.severity === severity));
};

/**
 * What the list cannot show while the tenant's checklist is not `ready`: its
 * account and Benutzungsordnung points. `null` once it is ready. A failed read
 * is said as a failure, never left to look like "Keine offenen Punkte".
 */
export const setupStateNotice = (state: ViewedSetupProgress['state'], t: TFunction): string | null => {
    if (state === 'failed') return t('onboarding_import.open_points.setup_failed');
    if (state === 'loading') return t('onboarding_import.open_points.setup_loading');
    return null;
};

/**
 * The list as plain text, to paste into a mail or a checklist: grouped by
 * level, one line per point. `notice` (`setupStateNotice`) heads it while the
 * account points are missing, and then the list never says "none".
 */
export const openPointsText = (
    points: OpenPoint[],
    t: TFunction,
    notice: string | null = null,
    title: string = t('onboarding_import.open_points.text_title'),
): string => {
    const lines = [title, ''];
    if (notice) lines.push(notice, '');
    for (const severity of SEVERITY_ORDER) {
        const group = points.filter((p) => p.severity === severity);
        if (group.length === 0) continue;
        const level = t(`onboarding_import.open_points.severity.${severity}`);
        const hint = t(`onboarding_import.open_points.severity_hint.${severity}`);
        lines.push(`${level} (${hint})`);
        for (const p of group) {
            const detail = p.detail ? `: ${p.detail}` : '';
            lines.push(`- ${p.title}${detail}`, `  ${p.consequence}`);
        }
        lines.push('');
    }
    if (points.length === 0 && !notice) lines.push(t('onboarding_import.open_points.none'), '');
    return lines.join('\n');
};
